import { Link } from 'react-router-dom';
import { ArrowRightLeft, ArrowDown, ArrowUp, CheckCircle, FileText, RotateCcw, Users, Shield } from 'lucide-react';
import { AreaDocLayout, DocDivider, ReferenceTable } from '../components/AreaDocLayout';
import { DocSection } from '../components/DocSection';
import { ConceptCard } from '../components/ConceptCard';
import { StepGuide } from '../components/StepGuide';
import { CatchballLoopDiagram } from '../components/diagrams/CatchballLoopDiagram';
import { useDiagramTheme } from '../components/diagrams/useDiagramTheme';

function AuthorityLaneDiagram() {
  const t = useDiagramTheme();
  return (
    <div className="w-full border p-5 overflow-x-auto transition-colors" style={{ background: t.canvasBg, borderColor: t.canvasBorder }}>
      <svg viewBox="0 0 680 160" className="w-full" style={{ minWidth: 520 }}>
        <defs>
          <marker id="al-down" markerWidth="7" markerHeight="7" refX="3" refY="6" orient="auto">
            <path d="M0,0 L6,0 L3,7 z" fill={t.amberBorder} />
          </marker>
          <marker id="al-up" markerWidth="7" markerHeight="7" refX="3" refY="1" orient="auto">
            <path d="M0,7 L6,7 L3,0 z" fill={t.greenBorder} />
          </marker>
        </defs>

        {/* Two lanes */}
        <rect x="10" y="10" width="660" height="60" rx="8" fill={t.amberBg} stroke={t.amberBorder} strokeWidth="1.5" />
        <text x="30" y="34" fontSize="10" fontWeight="700" fill={t.amberText} fontFamily="sans-serif">Ball Lane — Catchball</text>
        <text x="30" y="51" fontSize="8.5" fill={t.amberText} fontFamily="sans-serif">Requests · Proposals · Handoffs · Ratification · Publication — no ranking here</text>

        <rect x="10" y="90" width="660" height="60" rx="8" fill={t.indigoBg} stroke={t.indigoBorder} strokeWidth="1.5" />
        <text x="30" y="114" fontSize="10" fontWeight="700" fill={t.indigoText} fontFamily="sans-serif">Work Lane — Sessions</text>
        <text x="30" y="131" fontSize="8.5" fill={t.indigoText} fontFamily="sans-serif">Item pool · Criteria · Scoring · Force-ranking · Session results — no governance here</text>

        {/* Arrows between lanes */}
        <line x1="340" y1="72" x2="340" y2="88" stroke={t.amberBorder} strokeWidth="2" markerEnd="url(#al-down)" />
        <text x="355" y="83" fontSize="8" fill={t.amberText} fontFamily="sans-serif">Request down</text>

        <line x1="430" y1="88" x2="430" y2="72" stroke={t.greenBorder} strokeWidth="2" markerEnd="url(#al-up)" />
        <text x="445" y="83" fontSize="8" fill={t.greenText} fontFamily="sans-serif">Proposal up</text>
      </svg>
      <p className="text-center text-xs mt-1" style={{ color: t.canvasText }}>The two lanes are structurally separate — governance events happen in the ball lane, ranking happens in the work lane</p>
    </div>
  );
}

function GovernanceEventTable() {
  const events = [
    { event: 'Loop Opened', dir: '↓', actor: 'Authority', desc: 'A new loop begins. A request is sent to the team tier.' },
    { event: 'Request Received', dir: '→', actor: 'Team', desc: 'Team acknowledges the request and begins work.' },
    { event: 'Proposal Submitted', dir: '↑', actor: 'Team', desc: 'Team submits a ranked proposal with rationale.' },
    { event: 'Proposal Accepted', dir: '→', actor: 'Authority', desc: 'Authority accepts the proposal. Loop closes.' },
    { event: 'Revision Requested', dir: '↓', actor: 'Authority', desc: 'Authority sends the proposal back with feedback. New loop begins.' },
    { event: 'Ratification', dir: '→', actor: 'Authority', desc: 'Proposals across all tracks are ratified as a coherent set.' },
    { event: 'Publication', dir: '→', actor: 'System', desc: 'Ratified proposals are published as the priority list of record.' },
  ];

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--surface)] border-b border-[var(--rule)]">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-40">Event</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-8">Dir</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-24">Actor</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading">Description</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={e.event} className={i % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/50'}>
              <td className="px-4 py-2.5 text-xs font-semibold text-[var(--mustard)]">{e.event}</td>
              <td className="px-4 py-2.5 text-sm font-bold text-[var(--mustard)]">{e.dir}</td>
              <td className="px-4 py-2.5 text-xs text-[var(--text-ter)]">{e.actor}</td>
              <td className="px-4 py-2.5 text-xs text-[var(--text-sec)]">{e.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CatchballDocs() {
  return (
    <AreaDocLayout
      icon={ArrowRightLeft}
      iconBg="bg-[var(--mustard-dim)] border-[var(--mustard)]"
      iconColor="text-[var(--mustard)]"
      title="Catchball & Governance"
      subtitle="Authority choreography — requests flow down, proposals flow up, governance events record every transition."
      tag="Ball lane"
      tagColor="bg-[var(--mustard-dim)] text-[var(--mustard)]"
    >
      {/* Overview */}
      <DocSection
        title="Overview"
        description="Catchball is how authority moves through the system — not how ranking happens."
        accent="amber"
      >
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 text-sm text-[var(--mustard)] leading-relaxed">
          <strong>Catchball</strong> is the governance choreography pattern used within a <Link to="/prioritization/documentation/cycles" className="underline text-[var(--mustard)]">Cycle</Link>. It governs the flow of authority between tiers:
          the executive or authority tier sends requests down, teams do the ranking work in <Link to="/prioritization/documentation/sessions" className="underline text-[var(--mustard)]">sessions</Link>, then proposals flow back up.
          The authority tier accepts or requests revision. This cycle may repeat in multiple <strong>loops</strong> before ratification.
          <br /><br />
          <strong>Catchball does not perform ranking.</strong> Ranking happens in the work lane (sessions). Catchball governs what happens with the ranked outputs.
        </div>
      </DocSection>

      {/* Two-lane diagram */}
      <DocSection
        title="Two Lanes: Authority vs. Judgment"
        description="Catchball operates exclusively in the ball lane. Sessions operate in the work lane. The two never merge."
        accent="amber"
      >
        <AuthorityLaneDiagram />
      </DocSection>

      {/* Loop diagram */}
      <DocSection
        title="Catchball Loop Anatomy"
        description="One loop = one complete authority iteration: request → work → propose → accept or revise."
        accent="amber"
      >
        <CatchballLoopDiagram />
      </DocSection>

      <DocDivider />

      {/* Key concepts */}
      <DocSection title="Key Concepts" accent="amber">
        <div className="grid gap-3 md:grid-cols-2">
          <ConceptCard
            term="Catchball Loop"
            definition="One complete authority iteration within a cycle. Begins with a Request (downward handoff) and ends with a Proposal Submission (upward handoff). May span multiple tracks."
            icon={RotateCcw}
            color="amber"
          />
          <ConceptCard
            term="The Ball"
            definition="The governance payload moved during catchball. Contains direction (Request or Proposal), authority metadata, and tier holder information. Exists only in the ball lane."
            icon={ArrowRightLeft}
            color="amber"
          />
          <ConceptCard
            term="Request"
            definition="The downward form of the ball. Includes intent, desired output, constraints, guardrails, scope context, and time bounds. Sent from authority tier to execution tier."
            icon={ArrowDown}
            color="amber"
          />
          <ConceptCard
            term="Proposal"
            definition="A governance artifact produced per track per loop. References the final force-ranking session result, carries rationale and tradeoffs, and is subject to governance transitions."
            icon={ArrowUp}
            color="amber"
            tag="1 per track per loop"
          />
          <ConceptCard
            term="Handoff"
            definition="The atomic authority event where responsibility for advancing the ball transfers between tiers. Handoffs may be downward (request) or upward (proposal submission)."
            icon={Users}
            color="amber"
          />
          <ConceptCard
            term="Ratification"
            definition="The formal acceptance event where the authority tier accepts proposals across all tracks as a coherent set. Ratification precedes publication."
            icon={CheckCircle}
            color="amber"
          />
          <ConceptCard
            term="Governance Event"
            definition="An auditable record of every authority transition: loop opened, request sent, proposal submitted, accepted, revision requested, ratified, published."
            icon={FileText}
            color="amber"
            tag="Auditable"
          />
          <ConceptCard
            term="Governance Posture"
            definition="The current authority state of a Track, derived from the latest proposal state in the active loop. Not a field on the session or item — it belongs to the Track."
            icon={Shield}
            color="amber"
            tag="Per Track"
          />
        </div>
      </DocSection>

      <DocDivider />

      {/* Governance events */}
      <DocSection
        title="Governance Events"
        description="Every authority transition produces an immutable governance event. These form the complete audit trail for a cycle."
        accent="amber"
      >
        <GovernanceEventTable />
      </DocSection>

      <DocDivider />

      {/* Common tasks */}
      <DocSection title="Common Tasks" accent="amber">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <ArrowDown className="h-4 w-4 text-[var(--mustard)]" />
              <p className="text-sm font-semibold text-[var(--text-pri)] font-heading">Send a Request (Authority Tier)</p>
            </div>
            <StepGuide color="gray" steps={[
              { title: 'Open the Catchball panel for the cycle', description: 'Navigate to the cycle and open the Catchball tab.' },
              { title: 'Open a new loop', description: 'Select "Start Loop" or "New Request." Define the loop intent and which tracks are in scope.' },
              { title: 'Fill out the request', description: 'Add intent, desired outputs, constraints, guardrails, and time bounds.' },
              { title: 'Toss the ball', description: 'Submit the request. The ball is sent down to the execution tier. A governance event is recorded.', note: 'The team tier receives a notification and can begin work.' },
            ]} />
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <ArrowUp className="h-4 w-4 text-[var(--mustard)]" />
              <p className="text-sm font-semibold text-[var(--text-pri)] font-heading">Submit a Proposal (Team Tier)</p>
            </div>
            <StepGuide color="gray" steps={[
              { title: 'Complete ranking sessions', description: 'All configuration sessions must be complete. Run the force-ranking session for your Track. Verify the result.' },
              { title: 'Open the proposal form', description: 'In the Catchball panel, find the active loop for your track and open the proposal composer.' },
              { title: 'Reference the session result', description: 'Link the completed ranking session result to the proposal. This is the evidential basis.' },
              { title: 'Add rationale and tradeoffs', description: 'Explain the decisions, tradeoffs considered, and any constraints that shaped the ranking.' },
              { title: 'Submit the proposal', description: 'The ball is sent upward. A governance event is recorded. The authority tier is notified for review.' },
            ]} />
          </div>
        </div>
      </DocSection>

      <DocDivider />

      {/* Reference */}
      <DocSection title="Catchball Loop Field Reference" accent="amber">
        <ReferenceTable rows={[
          { field: 'id', type: 'uuid', description: 'Unique loop identifier.' },
          { field: 'cycle_id', type: 'uuid', description: 'The cycle this loop belongs to.' },
          { field: 'loop_number', type: 'integer', description: 'Sequential loop number within the cycle (1-indexed).' },
          { field: 'status', type: 'enum', description: 'open | proposal_submitted | accepted | revision_requested | closed' },
          { field: 'request_sent_at', type: 'timestamp?', description: 'When the request was sent down.' },
          { field: 'proposal_submitted_at', type: 'timestamp?', description: 'When the proposal was submitted up.' },
          { field: 'accepted_at', type: 'timestamp?', description: 'When the proposal was accepted by the authority tier.' },
          { field: 'workspace_id', type: 'uuid', description: 'Workspace this loop belongs to.' },
        ]} />
      </DocSection>
    </AreaDocLayout>
  );
}
